import { getCashFlow, getProfitAndLoss } from "../../apiClient";
import { formatDateForAPI } from "../dateUtils";
import { sumByCategory } from "../collectionUtils";
import { pickDimensionFilters } from "../dimensionParams";
import { formatReportApiError, isRequestCancelled } from "../errorUtils";
import { getMockReportData } from "../mockData";
import {
  calculateAccountsPayable,
  calculateDaysPayableOutstanding,
  calculatePayableTurnover,
  getPayablesDetails,
} from "../payablesMetrics";
import { formatCashFlowData } from "./cashFlow";
import { formatVerticalIncomeData } from "./profitAndLoss";

const calculateCashOutflow = (items = []) =>
  items.reduce(
    (total, item) => total + Math.abs(Math.min(item.amount || item.cash_flow || 0, 0)),
    0
  );

const calculatePaymentsMade = (cashFlowData = {}) => {
  let totalPayments = 0;
  let supplierPayments = 0;

  (cashFlowData.operating || []).forEach((item) => {
    const rawAmount = item.amount || item.cash_flow || 0;
    const description = item.description?.toLowerCase() || "";

    if (rawAmount < 0) {
      totalPayments += Math.abs(rawAmount);
      if (
        description.includes("مورد") ||
        description.includes("supplier") ||
        description.includes("دفع") ||
        description.includes("payment")
      ) {
        supplierPayments += Math.abs(rawAmount);
      }
    }
  });

  return {
    totalPayments,
    supplierPayments,
    otherPayments: totalPayments - supplierPayments,
    supplierPaymentPercentage: totalPayments > 0 ? (supplierPayments / totalPayments) * 100 : 0,
  };
};

const extractVendorName = (expense) => {
  const name = expense.name || expense.account_name || "";
  const description = expense.description || "";

  if (name.includes("مورد") || name.toLowerCase().includes("supplier")) {
    return name;
  }

  const match =
    description.match(/مورد[:\s]*([^,.\s]+)/i) ||
    description.match(/supplier[:\s]*([^,.\s]+)/i);

  return match ? match[1] : null;
};

const getTopVendors = (pnlData = {}) =>
  Object.entries(
    (pnlData.expenses || []).reduce((vendors, expense) => {
      const vendor = extractVendorName(expense);
      if (!vendor) return vendors;

      if (!vendors[vendor]) {
        vendors[vendor] = { totalAmount: 0, transactionCount: 0, lastTransaction: expense.date || "" };
      }

      vendors[vendor].totalAmount += expense.amount || 0;
      vendors[vendor].transactionCount += 1;
      if (expense.date && expense.date > vendors[vendor].lastTransaction) {
        vendors[vendor].lastTransaction = expense.date;
      }

      return vendors;
    }, {})
  )
    .map(([vendor, data]) => ({ vendor, ...data }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
    .slice(0, 10);

const formatPayablesData = (pnlData, cashFlowData) => {
  const totalPayables = calculateAccountsPayable(pnlData);
  const payableTurnover = calculatePayableTurnover(pnlData);
  const operatingCashOutflow = calculateCashOutflow(cashFlowData.operating || []);
  const revenue = sumByCategory(pnlData.revenue || []);
  const daysPayableOutstanding = calculateDaysPayableOutstanding(pnlData);

  return {
    summary: {
      totalPayables,
      costOfGoodsSold: sumByCategory(pnlData.cost_of_goods_sold || []),
      operatingCashOutflow,
      daysPayableOutstanding,
      payableTurnover,
      payableToRevenueRatio: revenue > 0 ? (totalPayables / revenue) * 100 : 0,
    },
    metrics: {
      dpo: daysPayableOutstanding,
      turnover: payableTurnover,
      paymentEfficiency: operatingCashOutflow > 0 ? (totalPayables / operatingCashOutflow) * 100 : 0,
      averagePaymentPeriod: daysPayableOutstanding,
      payableVelocity: payableTurnover > 0 ? 365 / payableTurnover : 0,
    },
    details: getPayablesDetails(pnlData, cashFlowData),
    cashFlowImpact: {
      paymentsMade: calculatePaymentsMade(cashFlowData),
      cashFlowCoverage: operatingCashOutflow > 0 ? totalPayables / operatingCashOutflow : 0,
    },
    vendorAnalysis: {
      topVendors: getTopVendors(pnlData),
    },
    combinedData: {
      profitAndLoss: formatVerticalIncomeData(pnlData),
      cashFlow: formatCashFlowData(cashFlowData),
    },
  };
};

export const handlePayablesReport = async (
  filters,
  { rangeEndDate, rangeStartDate },
  language = "ar",
  options = {}
) => {
  try {
    const startTime = Date.now();
    const [pnlResponse, cashFlowResponse] = await Promise.all([
      getProfitAndLoss({
        currency: filters.currency,
        date_after: formatDateForAPI(rangeStartDate),
        date_before: formatDateForAPI(rangeEndDate),
        compare_period_unit: filters.compare_period_unit || "month",
        transaction_type: "expense",
        ...(filters.category && { category: filters.category }),
        ...pickDimensionFilters(filters),
      }, { signal: options.signal }),
      getCashFlow({
        currency: filters.currency,
        date_after: formatDateForAPI(rangeStartDate),
        date_before: formatDateForAPI(rangeEndDate),
        compare_period_unit: filters.compare_period_unit || "month",
        cash_flow_type: filters.cash_flow_type || "operating",
        ...pickDimensionFilters(filters),
      }, { signal: options.signal }),
    ]);

    return {
      success: true,
      reportType: "payables",
      isRealData: true,
      originalData: {
        profitAndLoss: pnlResponse.data,
        cashFlow: cashFlowResponse.data,
      },
      data: formatPayablesData(pnlResponse.data, cashFlowResponse.data),
      requestInfo: {
        timestamp: startTime,
        duration: Date.now() - startTime,
        apisCalled: ["profitAndLoss", "cashFlow"],
        requestCount: 2,
      },
    };
  } catch (error) {
    if (isRequestCancelled(error)) throw error;
    console.error("Payables Report API Error:", error);
    return {
      success: false,
      reportType: "payables",
      isRealData: false,
      data: getMockReportData("payables", filters),
      error: formatReportApiError(error, language),
    };
  }
};
